import type { Task } from '@/data/types';
import { parseExpiryDetail } from './fleetDocs';
import { documentKindKey } from './loadDocs';

type T = (key: string, values?: Record<string, unknown>) => string;

/**
 * Every value of `public.task_source` (0002, 0039, 0046, 0085, 0143). The SQL writers store an ENGLISH title
 * and detail (load_reject 0040, app.enqueue_dunning 0049, delete_my_account 0086/0093), so the portal
 * renders each machine source from copy; only `manual` keeps its stored title, which the office typed.
 */
export const TASK_SOURCES = [
  'manual',
  'document_expiry',
  'load_rejected',
  'invoice_overdue',
  'driver_account_deleted',
  // invoice-pdf (0143): the weekly invoice was not emailed (the close found no address, or the send
  // failed), and the broker invoice email failed.
  'invoice_delivery',
  'broker_invoice_delivery',
] as const;

// The detail formats of the writers (format() strings in SQL). A detail that does not match falls back
// to copy without the parsed value, never to the English text.
const DELETED_DRIVER = /^(.+?) deleted their Coreway app account\./; // delete_my_account
const OVERDUE = /^Invoice (.+) is (\d+) day\(s\) overdue/; // app.enqueue_dunning
const NOT_EMAILED = /^Invoice (.+) was not emailed$/; // app.invoice_send_core, the Friday close (0146)
const EMAIL_FAILED = /^Invoice (.+) email failed$/; // app.document_email_mark_failed (0146)

/** The line the Upcoming Tasks card shows for a task, in the UI language. */
export function taskTitle(task: Pick<Task, 'source' | 'title' | 'detail' | 'loadId'>, loadNumber: string | null, t: T): string {
  switch (task.source) {
    case 'load_rejected':
      if (!loadNumber) return t('tasks.sources.loadRejectedGeneric');
      return task.detail
        ? t('tasks.sources.loadRejected', { load: loadNumber, reason: task.detail })
        : t('tasks.sources.loadRejectedNoReason', { load: loadNumber });
    case 'driver_account_deleted': {
      const driver = task.detail?.match(DELETED_DRIVER)?.[1] ?? null;
      if (!loadNumber) return t('tasks.sources.driverAccountDeletedGeneric');
      return driver
        ? t('tasks.sources.driverAccountDeleted', { load: loadNumber, driver })
        : t('tasks.sources.driverAccountDeletedNoDriver', { load: loadNumber });
    }
    case 'invoice_overdue': {
      const m = task.detail?.match(OVERDUE);
      return m ? t('tasks.sources.invoiceOverdue', { invoice: m[1], count: Number(m[2]) }) : t('tasks.sources.invoiceOverdueGeneric');
    }
    case 'invoice_delivery': {
      const none = task.title.match(NOT_EMAILED);
      if (none) return t('tasks.sources.invoiceNotEmailed', { invoice: none[1] });
      const failed = task.title.match(EMAIL_FAILED);
      return failed ? t('tasks.sources.invoiceEmailFailed', { invoice: failed[1] }) : t('tasks.sources.invoiceDeliveryGeneric');
    }
    case 'broker_invoice_delivery': {
      const failed = task.title.match(EMAIL_FAILED);
      if (!failed) return t('tasks.sources.brokerInvoiceDeliveryGeneric');
      return loadNumber
        ? t('tasks.sources.brokerInvoiceEmailFailed', { invoice: failed[1], load: loadNumber })
        : t('tasks.sources.brokerInvoiceEmailFailedNoLoad', { invoice: failed[1] });
    }
    case 'document_expiry': {
      // app.sweep_document_expiry (0128): `{owner_kind} {owner}: {kind} {expires|expired} on {date}`.
      const d = parseExpiryDetail(task.detail);
      if (!d) return t('tasks.sources.documentExpiry');
      return t(d.expired ? 'tasks.sources.documentExpired' : 'tasks.sources.documentExpiring', {
        kind: t(documentKindKey(d.kind)),
        owner: d.ownerName,
        date: d.date,
      });
    }
    default:
      // `manual`, and a source newer than this build: the stored title.
      return task.title;
  }
}
